import type { z } from 'zod';
import { reportSchemaMismatch } from '@/modules/report-schema-mismatch';

// The in-band degraded variant a row-level parse produces for a row that
// failed its schema: the row degrades ALONE (siblings stay typed).
// Consumers narrow on status === 'malformed' and render the flagged-row
// treatment — a malformed row must stay VISIBLE in lists (money-adjacent
// rows never silently vanish) but must be EXCLUDED from every aggregate and
// count.
export type MalformedRow = {
  status: 'malformed';
  id: string;
  // unread-field-allow: diagnostic payload for devtools inspection of a flagged row — deliberately never read by app code (the reporter already logged it)
  raw: unknown;
};

// `id` salvages from the raw row's id (string or number) so list keys stay
// stable; a row without a usable id gets a synthetic per-index key.
export function salvageRowId(raw: unknown, index: number): string {
  // Object(raw) never throws (null → {}, primitives box), so the salvage
  // path has no failure mode of its own.
  const { id } = Object(raw) as { id?: unknown };
  return typeof id === 'string' || typeof id === 'number' ?
      String(id)
    : `malformed-${index}`;
}

// Row-level parsing: every row parses through `schema` on its own; a
// failing row becomes a MalformedRow (one report per bad row through
// @/modules/report-schema-mismatch — the per-source/key fingerprint
// dedupes) while its siblings stay strictly typed.
export function parseRowsWithDegrade<S extends z.ZodType>(
  source: Parameters<typeof reportSchemaMismatch>[0],
  key: string,
  schema: S,
  rows: unknown[],
): (z.output<S> | MalformedRow)[] {
  return rows.map((raw, index) => {
    const parsed = schema.safeParse(raw);
    if (parsed.success) return parsed.data;
    reportSchemaMismatch(source, key, parsed.error, raw);
    return { status: 'malformed', id: salvageRowId(raw, index), raw };
  });
}
